"use client";

import React, { createContext, useContext, ReactNode, useState, useRef } from "react";
import PullToRefresh from "../common/PullToRefresh";

interface PullToRefreshConfig {
  enabled?: boolean;
  threshold?: number;
  resistance?: number;
  distanceToRefresh?: number;
  onRefresh?: () => Promise<void>;
}

interface PullToRefreshContextValue {
  registerRefreshHandler: (handler: () => Promise<void>) => void;
  unregisterRefreshHandler: () => void;
  isRefreshing: boolean;
}

const PullToRefreshContext = createContext<PullToRefreshContextValue | undefined>(undefined);

export const usePullToRefreshContext = () => {
  const context = useContext(PullToRefreshContext);
  if (!context) {
    throw new Error("usePullToRefreshContext must be used within PullToRefreshProvider");
  }
  return context;
};

interface PullToRefreshProviderProps {
  children: ReactNode;
  config?: PullToRefreshConfig;
}

// Platform detection helper
const getPlatformInfo = () => {
  if (typeof window === "undefined") {
    return {
      shouldUsePullToRefresh: false,
      isIOS: false,
      isAndroid: false,
      isPWA: false,
    };
  }

  const userAgent = navigator.userAgent;
  const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent);
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isIOS = /iPhone|iPad|iPod/.test(userAgent) && !(window as any).MSStream;
  const isAndroid = /Android/i.test(userAgent);

  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    (window.navigator as any).standalone === true;

  const shouldUsePullToRefresh = isMobileDevice && isTouchDevice && (
    (isIOS && isPWA) ||
    (isAndroid && isPWA) ||
    (isPWA)
  );

  return {
    shouldUsePullToRefresh,
    isIOS,
    isAndroid,
    isPWA,
  };
};

/**
 * Backward compatible wrapper for the new PullToRefresh component
 */
export const PullToRefreshProvider: React.FC<PullToRefreshProviderProps> = ({
  children,
  config = {},
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshHandlerRef = useRef<(() => Promise<void>) | null>(null);

  const registerRefreshHandler = (handler: () => Promise<void>) => {
    refreshHandlerRef.current = handler;
  };

  const unregisterRefreshHandler = () => {
    refreshHandlerRef.current = null;
  };

  const { shouldUsePullToRefresh } = getPlatformInfo();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const handler = refreshHandlerRef.current || config.onRefresh;
      if (handler) {
        await handler();
      } else {
        // Default - reload page
        await new Promise(resolve => setTimeout(resolve, 1000));
        window.location.reload();
      }
    } catch (error) {
      console.error("Pull to refresh error:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const isEnabled = shouldUsePullToRefresh && config.enabled !== false;

  return (
    <PullToRefreshContext.Provider
      value={{
        registerRefreshHandler,
        unregisterRefreshHandler,
        isRefreshing,
      }}
    >
      <PullToRefresh
        onRefresh={handleRefresh}
        enabled={isEnabled}
        threshold={config.distanceToRefresh || config.threshold || 80}
        resistance={config.resistance || 2.5}
      >
        {children}
      </PullToRefresh>
    </PullToRefreshContext.Provider>
  );
};